import type { BibleFootnote, BibleOutline, LoadedVerse } from "./bible";

export interface RecoveryFootnoteMark {
  seq: string;
  location: number;
  note: string;
  _id?: string;
  verse?: string;
  flag?: string;
}

export interface RecoveryUnit {
  key: string;
  book: string;
  chapter: string;
  verse: string;
  bookIndex: string;
  text: string;
  isPrologue: boolean;
  isMergedNote: boolean;
  prevVerse: string | null;
  footnotes: RecoveryFootnoteMark[];
  outlines: BibleOutline[];
  sampleVerse: LoadedVerse;
}

// 恢复注解版的分段顺序：上(1)、中(3)、下(2)；未分段时只有 0
export const FLAG_ORDER = ["0", "1", "3", "2"];

export function buildBookIndexMap(books: { _id: string; book_id: string }[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const b of books) {
    if (b.book_id) map.set(b.book_id, b._id);
    if (b._id) map.set(b._id, b._id);
  }
  return map;
}

export function buildRecoveryUnits(
  verses: LoadedVerse[],
  footnotes: BibleFootnote[],
  outlines: BibleOutline[],
  bookIndexMap: Map<string, string>
): RecoveryUnit[] {
  const groups = new Map<string, LoadedVerse[]>();
  const order: string[] = [];
  for (const v of verses) {
    const key = `${v.Book}|${v.Chapter}|${v.Verse}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(v);
  }

  const units: RecoveryUnit[] = [];
  for (const key of order) {
    const parts = [...groups.get(key)!].sort((a, b) => {
      const fa = FLAG_ORDER.indexOf(String(a.Flag || "0"));
      const fb = FLAG_ORDER.indexOf(String(b.Flag || "0"));
      return (fa < 0 ? 99 : fa) - (fb < 0 ? 99 : fb);
    });
    const [book, chapter, verse] = key.split("|");
    const bookIndex = bookIndexMap.get(book) || bookIndexMap.get(book.toLowerCase()) || "";
    const isPrologue = verse === "0";
    const hasMergedMarker = parts.some((p) => String(p.Text).trim() === "a");
    const realParts = parts.filter((p) => String(p.Text).trim() !== "a");
    const text = realParts.map((p) => p.Text).join("");

    const marks: RecoveryFootnoteMark[] = [];
    let offset = 0;
    for (const p of realParts) {
      const pFlag = String(p.Flag || "0");
      const fns = footnotes
        .filter(
          (f) =>
            String(f.book_index) === bookIndex &&
            String(f.chapter) === chapter &&
            String(f.section) === verse &&
            String(f.flag) === pFlag
        )
        .sort((x, y) => (parseInt(x.location, 10) || 0) - (parseInt(y.location, 10) || 0));
      for (const fn of fns) {
        if (!fn.note.trim()) continue;
        const loc = offset + (parseInt(fn.location, 10) || 1);
        if (marks.some((m) => m.location === loc && m.seq === fn.seq)) continue;
        marks.push({ seq: fn.seq, location: loc, note: fn.note, _id: fn._id, verse: String(fn.section), flag: pFlag });
      }
      offset += String(p.Text).length + 1;
    }
    marks.sort((a, b) => a.location - b.location || a.seq.localeCompare(b.seq));

    const unitOutlines = outlines
      .filter(
        (o) =>
          String(o.book_index) === bookIndex &&
          String(o.chapter) === chapter &&
          String(o.section) === verse
      )
      .sort(
        (x, y) =>
          (parseInt(x.level, 10) || 0) - (parseInt(y.level, 10) || 0) ||
          (parseInt(x.section, 10) || 0) - (parseInt(y.section, 10) || 0)
      );

    const prevVerse = hasMergedMarker && units.length > 0 ? units[units.length - 1].verse : null;
    units.push({
      key: `${book}|${chapter}|${verse}`,
      book,
      chapter,
      verse,
      bookIndex,
      text,
      isPrologue,
      isMergedNote: hasMergedMarker,
      prevVerse,
      footnotes: marks,
      outlines: unitOutlines,
      sampleVerse: parts[0],
    });
  }
  return units;
}
